import { observer } from 'mobx-react-lite'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSelector } from 'react-redux'
import { RootState, UserRole } from 'types'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { Section } from 'blaze-components/WorkspaceDashboardSidebar/components/Section/Section'
import cx from 'classnames'
import NavMenuStyles from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/NavMenu.module.scss'
import Styles from 'blaze-components/WorkspaceDashboardSidebar/WorkspaceDashboardSidebarContent.module.scss'
import { CanView } from 'components/Can'
import { NavMenuOrganicCampaignsItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuOrganicCampaignsItem'
import { NavMenuItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/NavMenuItem/NavMenuItem'
import { Calendar1 } from 'blaze-ui/icons/24'
import { generatePath } from 'react-router-dom'
import {
  BOOKING_PATH,
  COMPETITOR_SPIKE_PATH,
  GOOGLE_ADS_PATH,
  LANDING_PAGES_PATH,
  LOCAL_SERVICE_ADS_PATH,
  NOTIFICATIONS_PATH,
  ORGANIC_CAMPAIGNS_PATH,
  PAID_ADS_PATH,
  SDR_PATH,
  SEO_RELEVANCE_PATH,
  TOOLS_PATH,
} from 'paths'
import { NavMenuHomeItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuHomeItem'
import { NavMenuAutopilotIntegrationsItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuAutopilotIntegrationsItem'
import { NavMenuReputationItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuReputationItem'
import { NavMenuBrandKitsItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuBrandKitsItem'
import { NavMenuContentPreferencesItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuContentPreferencesItem'
import { BarChartSquare, Bell, Cursor04, Globe, Target2, Templates, UserProfileGroup, Wrench } from 'blaze-ui/icons'
import { NavMenuLocalSeoItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuLocalSeoItem'
import { Pill } from 'components/Pill/Pill'
import { FlipperFlag } from 'components/FlipperFlag/FlipperFlag'

export const NewNavMenu = observer(() => {
  const workspace = useCurrentWorkspace()
  const { isMobile } = useResponsiveDesign()

  const { sidebarLocked, floatingSidebar } = useSelector(
    ({ sidebar: { locked: sidebarLocked }, floatingSidebar }: RootState) => ({
      floatingSidebar,
      sidebarLocked,
    }),
  )

  if (!workspace) return null

  const closeMobileSidebar = () => {
    if (isMobile && (sidebarLocked || floatingSidebar)) WorkspaceService.hideSidebar()
  }

  return (
    <div className={Styles.nav}>
      <ul className={NavMenuStyles.navItemsList}>
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <li>
            <NavMenuHomeItem workspace={workspace} onPress={closeMobileSidebar} />
          </li>
        </CanView>
      </ul>

      <Section title="Awareness">
        <ul className={cx(NavMenuStyles.navItemsList, Styles.sectionNavItemsList)}>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuOrganicCampaignsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
            </li>
          </CanView>
          <FlipperFlag enabled={'map_pack'}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuLocalSeoItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>
          </FlipperFlag>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuItem
                icon={Globe}
                activeIcon={Globe}
                to={generatePath(SEO_RELEVANCE_PATH, { workspaceId: workspace.id })}
                onPress={closeMobileSidebar}
                label="SEO/AEO"
              >
                <FlipperFlag enabled={'seo_aeo'}>
                  <Pill className={NavMenuStyles.betaPill} size="sm">
                    Beta
                  </Pill>
                </FlipperFlag>
              </NavMenuItem>
            </li>
          </CanView>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuItem
                icon={Cursor04}
                activeIcon={Cursor04}
                to={generatePath(PAID_ADS_PATH, { workspaceId: workspace.id })}
                onPress={closeMobileSidebar}
                label="Paid Social"
              />
            </li>
          </CanView>
          <FlipperFlag enabled="google_ads_integration">
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuItem
                  icon={BarChartSquare}
                  activeIcon={BarChartSquare}
                  to={generatePath(GOOGLE_ADS_PATH, { workspaceId: workspace.id })}
                  onPress={closeMobileSidebar}
                  label="Paid Search"
                >
                  <Pill className={NavMenuStyles.betaPill} size="sm">
                    Beta
                  </Pill>
                </NavMenuItem>
              </li>
            </CanView>
          </FlipperFlag>
        </ul>
      </Section>

      <Section title="Conversion">
        <ul className={cx(NavMenuStyles.navItemsList, Styles.sectionNavItemsList)}>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuItem
                icon={Templates}
                activeIcon={Templates}
                to={generatePath(LANDING_PAGES_PATH, { workspaceId: workspace.id })}
                onPress={closeMobileSidebar}
                label="Landing Pages"
              />
            </li>
          </CanView>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuItem
                icon={UserProfileGroup}
                activeIcon={UserProfileGroup}
                to={generatePath(SDR_PATH, { workspaceId: workspace.id })}
                onPress={closeMobileSidebar}
                label="AI Receptionist"
              />
            </li>
          </CanView>
          <CanView as={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuItem
                icon={Bell}
                activeIcon={Bell}
                to={generatePath(NOTIFICATIONS_PATH, { workspaceId: workspace.id })}
                onPress={closeMobileSidebar}
                label="Notifications"
              />
            </li>
          </CanView>
          <FlipperFlag disabled={'hide_booking'}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuItem
                  icon={Calendar1}
                  activeIcon={Calendar1}
                  to={generatePath(BOOKING_PATH, { workspaceId: workspace.id })}
                  onPress={closeMobileSidebar}
                  label="Booking"
                />
              </li>
            </CanView>
          </FlipperFlag>
          <FlipperFlag enabled={'local_service_ads'}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuItem
                  icon={Target2}
                  activeIcon={Target2}
                  to={generatePath(LOCAL_SERVICE_ADS_PATH, { workspaceId: workspace.id })}
                  onPress={closeMobileSidebar}
                  label="Local Service Ads"
                >
                  <Pill className={NavMenuStyles.betaPill} size="sm">
                    Beta
                  </Pill>
                </NavMenuItem>
              </li>
            </CanView>
          </FlipperFlag>
          <FlipperFlag enabled={'reputation_management'}>
            <CanView as={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuReputationItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>
          </FlipperFlag>
        </ul>
      </Section>

      <Section title="Settings">
        <ul className={cx(NavMenuStyles.navItemsList, Styles.sectionNavItemsList)}>
          <FlipperFlag disabled={'hide_meta_strategy'}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuItem
                  icon={Wrench}
                  activeIcon={Wrench}
                  to={generatePath(TOOLS_PATH, { workspaceId: workspace.id })}
                  onPress={closeMobileSidebar}
                  label="Meta Strategy"
                />
              </li>
            </CanView>
          </FlipperFlag>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuContentPreferencesItem workspace={workspace} onPress={closeMobileSidebar} />
            </li>
          </CanView>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuBrandKitsItem workspace={workspace} onPress={closeMobileSidebar} />
            </li>
          </CanView>
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
            <li>
              <NavMenuAutopilotIntegrationsItem onPress={closeMobileSidebar} />
            </li>
          </CanView>
        </ul>
      </Section>
    </div>
  )
})
